import React, { useState } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItemButton,
	ListItemText,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { HELP_CENTER_INBOX_QUERY_KEY, searchHelpRequests } from '../../../api-data/iam/help-center';
import { public_service_provider_api_requests, SERVICE_PROVIDER_API } from '../../../api-data/service-provider';
import { NormalFilter } from '../../../api-data/util/baseDto';

// Holds the sessionId of the login that already saw the pop-up, so it shows once after every login.
const SEEN_KEY = 'openHelpRequestsPopupSeen';

const currentSession = () => {
	try {
		return localStorage.getItem('sessionId');
	} catch {
		return null;
	}
};

const wasSeen = () => {
	try {
		return !!currentSession() && localStorage.getItem(SEEN_KEY) === currentSession();
	} catch {
		return false;
	}
};

/** Home page pop-up for admins and field operators with the In Review provider and open Help request counts, once after every login (internal, English only). */
const OpenHelpRequestsPopup: React.FC = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const isFieldOperator = user?.userType === 'FIELD_OPERATOR';
	const isStaff = isFieldOperator || user?.userType === 'INTERNAL_USER';
	const inboxPath = isFieldOperator ? '/field-operator/help-center-inbox' : '/admin/help-center-inbox';
	const providersPath = isFieldOperator ? '/field-operator/service-providers' : '/admin/service-providers';
	const [dismissed, setDismissed] = useState(wasSeen);

	const { data } = useQuery(
		[...HELP_CENTER_INBOX_QUERY_KEY, 'open-popup'],
		() => searchHelpRequests({ status: 'OPEN', page: 0, size: 1, sort: 'createTimestamp,desc' }),
		{ enabled: isStaff && !dismissed }
	);

	// statusId 1 = in_review (service_provider.service_provider_status, V31)
	const { data: inReview } = useQuery(
		[SERVICE_PROVIDER_API, '/service-provider', 'in-review-popup'],
		() =>
			public_service_provider_api_requests.search('/service-provider/public/search?page=0&size=1', {
				operator: 'AND',
				criteria: [NormalFilter('statusId', 'EQUALS', 1)],
			}),
		{ enabled: isStaff && !dismissed }
	);

	const helpTotal = data?.metaData?.totalElements ?? 0;
	const inReviewTotal = inReview?.metaData?.totalElements ?? 0;

	const close = () => {
		try {
			const session = currentSession();
			if (session) localStorage.setItem(SEEN_KEY, session);
		} catch {
			// private mode: the pop-up may show again, which is harmless
		}
		setDismissed(true);
	};

	const go = (path: string) => {
		close();
		navigate(path);
	};

	return (
		<Dialog open={isStaff && !dismissed && (helpTotal > 0 || inReviewTotal > 0)} onClose={close} fullWidth maxWidth="sm">
			<DialogTitle>Pending Items</DialogTitle>
			<DialogContent dividers>
				<List disablePadding>
					<ListItemButton onClick={() => go(providersPath)}>
						<ListItemText primary={`Pending Reviews for Self Registrations - ${inReviewTotal}`} />
					</ListItemButton>
					<ListItemButton onClick={() => go(inboxPath)}>
						<ListItemText primary={`New Help Requests - ${helpTotal}`} />
					</ListItemButton>
				</List>
			</DialogContent>
			<DialogActions>
				<Button onClick={close}>Close</Button>
			</DialogActions>
		</Dialog>
	);
};

export default OpenHelpRequestsPopup;
